import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import LuminateDiscoveryFilters, {
  DEFAULT_LUMINATE_DISCOVERY_FILTERS,
} from '../components/snoop/LuminateDiscoveryFilters'
import { fetchLuminateAggSummary, fetchLuminateAggHistory, updateLuminateStatus } from '../api/luminateClient'
import {
  LUMINATE_EXCLUDED_STATUSES,
  LUMINATE_STATUS_OPTIONS,
  luminateRowsToRemovePredicate,
} from '../utils/snoop/statusOptions'
import { PLACEHOLDER_LUMINATE_AGG } from '../utils/snoop/placeholderColumns'

const AGG_TRUNCATE = ['Primary Artist', 'Imprint', 'Luminate Genres']
const AGG_NEC_COLUMNS = ['Primary Artist']
const AGG_HIDDEN = ['Main Artist IDs', 'Has Main Artist ID']

const LUMINATE_AGG_OUTREACH_CONFIG = {
  artistColumn: 'Primary Artist',
  confirmMultiArtistName: true,
}

/** Artist / label tiers (rollup is by primary artist, not track). */
const AGG_STATUS_OPTIONS = LUMINATE_STATUS_OPTIONS.filter((opt) => {
  const v = typeof opt === 'string' ? opt : opt.value
  return /Artist|Label/i.test(String(v))
})

export default function LuminateAggSummaryPage() {
  const [discoveryFilters, setDiscoveryFilters] = useState(DEFAULT_LUMINATE_DISCOVERY_FILTERS)
  const [hideNonEnglish, setHideNonEnglish] = useState(true)
  const [hideKnownArtists, setHideKnownArtists] = useState(true)
  const fetchData = useCallback(
    () =>
      fetchLuminateAggSummary({
        ...discoveryFilters,
        hideKnownArtists,
      }),
    [discoveryFilters, hideKnownArtists],
  )

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'luminate',
        uidColumn: 'Primary Artist',
        options: AGG_STATUS_OPTIONS,
        removeAfterSave: true,
        luminateCascadeColumns: {
          artistCol: 'Primary Artist',
          imprintCol: 'Imprint',
          songIdCol: null,
        },
        shouldRemoveRow: (status) => LUMINATE_EXCLUDED_STATUSES.has(String(status || '')),
        rowsToRemove: (status, ctx) =>
          luminateRowsToRemovePredicate(status, ctx, {
            artistCol: 'Primary Artist',
            imprintCol: 'Imprint',
            songIdCol: null,
          }),
        onSave: async ({ status, row }) => {
          await updateLuminateStatus({
            status,
            primaryArtist: row?.['Primary Artist'],
            imprint: row?.Imprint,
          })
        },
      },
    }),
    [],
  )

  return (
    <SummaryDashboard
      title="Luminate Agg"
      subtitle="Primary artist rollup · track count ≥ 2"
      fetchData={fetchData}
      defaultSort={[
        { column: 'Max Residual', direction: 'desc' },
        { column: 'Total Streams TW', direction: 'desc' },
      ]}
      searchPlaceholder="Search artist, imprint, status…"
      layout="unsigned"
      truncateColumns={AGG_TRUNCATE}
      hiddenColumns={AGG_HIDDEN}
      placeholderColumns={PLACEHOLDER_LUMINATE_AGG}
      editableStatus={editableStatus}
      enableOutreachColumn
      outreachConfig={LUMINATE_AGG_OUTREACH_CONFIG}
      enableHideNonEnglish
      hideNonEnglish={hideNonEnglish}
      onHideNonEnglishChange={setHideNonEnglish}
      showHideNonEnglishToggle={false}
      hideNonEnglishColumns={AGG_NEC_COLUMNS}
      enableGenreStartsWithFilter
      expandableHistory
      historyFetcher={async (row) => {
        return fetchLuminateAggHistory({
          primaryArtist: row?.['Primary Artist'],
          artistIds: row?.['Main Artist IDs'],
        })
      }}
      extraActions={
        <>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm hover:border-gray-300">
            <input
              type="checkbox"
              checked={hideKnownArtists}
              onChange={(event) => setHideKnownArtists(event.target.checked)}
              className="rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
            />
            Hide artists already in Artists tab
          </label>
          <LuminateDiscoveryFilters
            filters={discoveryFilters}
            onChange={setDiscoveryFilters}
            hideNonEnglish={hideNonEnglish}
            onHideNonEnglishChange={setHideNonEnglish}
          />
        </>
      }
    />
  )
}
